import { useEffect, useState, useRef } from "react";
import { Controller } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import {
  empAddState,
  empEditState,
  rowForEditEmployees,
  employeeFromOffice,
  isSavingEmployees,
  showKeyboard,
} from "../atoms/atoms";
import { useGetAllOffices } from "../../../OfficeSettings/hooks/useSFGetOffice";
import { useEmployeeStatus } from "../hooks/useEmployeeStatus";
import { useEmployeeType } from "../hooks/useEmployeeType";
import { useEmploymentType } from "../hooks/useEmploymentType";
import { useEmployeeTitle } from "../hooks/useEmployeeTitle";
import { useEmployeeDepartment } from "../hooks/useEmployeeDepartment";
import { getEmployeesByFullName } from "../../../../../actions/Employees";

import useEditEmployee from "../hooks/useEditEmployee";
import useAddEmployees from "../hooks/useAddEmployee";

import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import {
  FIELDPERMISSIONS,
  PERMISSIONS,
} from "../../../../PermissionsConstants";
import { toast } from "components/toastNotification";
import CustomLoader from "components/CustomLoader";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import TextInputLabelPhone from "components/TextInputLabelPhone";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "@material-ui/core";
import { useEmployeeTerminationStatus } from "../hooks/useEmployeeTerminationStatus";
import { useEmployeeTerminationReason } from "../hooks/useEmployeeTerminationReason";
import TextInputHookFormDisabled from "components/TextInputHookFormDisabled";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import SelectHookFormSingle from "components/SelectHookFormSingle";
import SubmitHookForm from "components/SubmitHookForm";
import CancelButton from "components/CancelButton";
import TextInputLabelHookForm from "components/TextInputLabelHookForm";
import useGetEmailDomains from "../../../../Utilities/EmployeeConfiguration/EmailDomains/hooks/useGetEmailDomains";
import { accountAtom } from "../../../../Login/atoms/AccountAtom";
import useSendEmail from "./../hooks/useSendEmail";
import SelectHookFormMulti from "components/SelectHookFormMulti";
import { useGetReportsTo } from "../hooks/useEmployeeReportsTo";
import { useEmployeeBilingual } from "../hooks/useEmployeeBilingual";
import { useGetAcquisitionName } from "../hooks/useGetAcquisitionNameList";
import TextInputHookFormWithKB from "components/TextInputHookFormWithKB";
import { KeyboardCapslock } from "@material-ui/icons";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { useGetHomeCompanies } from "../hooks/useGetHomeCompanies";
import { useGetSeparationType } from "../hooks/useGetSeparationType";
import { useGetSeparationReason } from "../hooks/useGetSeparationReason";
import { useGetRehirable } from "../hooks/useGetRehirable";
import { getStateCode } from "utils/mexicoStateCodeMapping";
import { useGetHierarchyOpts } from "../hooks/useGetHierarchyOpts";
import CheckboxInlineHookForm from "components/CheckboxInlineHookForm";
import useSendHierarchyEmail from "modules/CarrierAgreementMgmt/Hierarchy/hooks/useSendHierarchyEmail";
import ReactTooltip from "react-tooltip";
import PropTypes from "prop-types";

const salutationOptions = [
  { value: "Mr.", label: "Mr." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Miss", label: "Miss" },
  { value: "Dr.", label: "Dr." },
];
const reportsStatusOpt = [
  { value: "Active", label: "Active" },
  { value: "InActive", label: "InActive" },
];
const libraries = ["places"];
const EmployeeForm = ({
  reportsToOptions,
  employeesOpts,
  isReportsToOpsLoading,
  overrideGroupOpts,
  setOnClose,
  onClickCancelShow,
  hasUSAdd,
  mexicoAdd,
  onSaveSuccess,
}) => {
  const queryClient = useQueryClient();
  const [showFromOffice] = useRecoilState(employeeFromOffice);
  const [saveModal, setSaveModal] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [newData, setNewData] = useState([]);
  const [onLeave, setOnLeave] = useState(false);
  const [titleChanged, setTitleChanged] = useState(false);
  const [locationChanged, setLocationChanged] = useState(false);
  const [fromLeave, setFromLeave] = useState(false);
  const [wasTerminated, setWasTerminated] = useState(false);
  const [rehire, setRehire] = useState(false);
  const [isAddState, setAddState] = useRecoilState(empAddState);
  const [isEditState, setEditState] = useRecoilState(empEditState);
  const [rowForEdit, setRowToEdit] = useRecoilState(rowForEditEmployees);
  const [isSaving, setIsSaving] = useRecoilState(isSavingEmployees);
  const [isKeyboardCaps, showIsKeyboardCaps] = useState(false);
  const [displayKeyboard, setDisplayKeyboard] = useRecoilState(showKeyboard);
  const isSubmittingRef = useRef(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const { mutate: EditEmployee } = useEditEmployee();
  const AddEmployee = useAddEmployees(onSaveSuccess);
  const { mutateAsync: sendEmail } = useSendEmail();
  const { mutateAsync: sendHierarchyEmail } = useSendHierarchyEmail();

  const viewContactPermission =
    PERMISSIONS.permission_employee_contactdetails_view;
  const addContactPermission =
    PERMISSIONS.permission_employee_contactdetails_add;
  const editContactPermission =
    PERMISSIONS.permission_employee_contactdetails_edit;
  const viewAdditionalPermission =
    PERMISSIONS.permission_employee_additionaldetails_view;
  const addAdditionalPermission =
    PERMISSIONS.permission_employee_additionaldetails_add;
  const editAddtionalPermission =
    PERMISSIONS.permission_employee_additionaldetails_edit;
  const viewTrainingPermission = PERMISSIONS.permission_employee_training_view;
  const userPermissions = useRecoilValue(permissionsSelector);
  const user = useRecoilValue(accountAtom);
  const permissionContactEdit =
    userPermissions.includes(editContactPermission) && isEditState;
  const permissionContactAdd =
    userPermissions.includes(addContactPermission) && isAddState;
  const permissionAdditionalDetailsEdit =
    userPermissions.includes(editAddtionalPermission) && isEditState;
  const permissionAdditionalDetailsAdd =
    userPermissions.includes(addAdditionalPermission) && isAddState;
  const acquisitionName =
    FIELDPERMISSIONS.fieldpermission_employee_acquisitionname;
  //Field Permissions
  const updateApprovedField =
    FIELDPERMISSIONS.fieldpermission_employee_updateapproved;

  const updateApprovedFieldReadOnly =
    FIELDPERMISSIONS.fieldpermission_employee_updateapprovedReadOnly;
  const isSeparationType =
    FIELDPERMISSIONS.fieldpermission_employee_separationtype;
  const isSeparationReason =
    FIELDPERMISSIONS.fieldpermission_employee_separationtype;
  const isRehirable = FIELDPERMISSIONS.fieldpermission_employee_rehirable;

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    getValues,
    setError,
  } = useForm({
    defaultValues: { ...rowForEdit },
  });

  //DropDown Options
  const isMexico = watch("isMexico");

  // Set isMexico value
  useEffect(() => {
    if (!isAddState) return;

    if (hasUSAdd && !mexicoAdd) {
      setValue("isMexico", false);
    } else if (!hasUSAdd && mexicoAdd) {
      setValue("isMexico", true);
    }
  }, [isAddState, hasUSAdd, mexicoAdd, setValue]);

  const [selectedOfficeStatus, setSelectedOfficeStatus] = useState("");
  const { data: offices, isLoading: officeIsLoading } = useGetAllOffices();

  const filterOffice = (x) => {
    const hasValidOfficeCode = !!x.officeCode;

    const officeTypeFilter = isMexico
      ? true
      : user?.isFranchise && user?.isNonFranchise
        ? watch("employeeTypeName")?.includes("Franchise")
          ? x.officeType === "Franchise"
          : x.officeType !== "Franchise"
        : user?.isFranchise
          ? x.officeType === "Franchise"
          : x.officeType !== "Franchise";

    const countryFilter = isMexico
      ? x.country?.toLowerCase() === "mexico"
      : x.country?.toLowerCase() !== "mexico";

    return hasValidOfficeCode && officeTypeFilter && countryFilter;
  };

  const sfOfficeNameOpt = offices
    ?.filter(filterOffice)
    .map((x) => ({
      name: x.businessOperation,
      id: x.officeCode,
      value: x.officeName,
      label: x.officeName,
      phone: x.phone,
      fullAddress: x.fullAddress,
      street: x.address,
      city: x.city,
      state: x.state,
      stateCode: x.stateCode,
      zipcode: x.zipCode,
      country: x.country,
      branchManager: x.branchManagerName,
      branchEmpNumber: x.branchEmpNumber,
      brancManagerId: x.branchManagerId,
      officeAcquisition: x.acquisitionName,
      officeStatus: x.officeStatus,
    }))
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const sfOfficeCodeOpt = offices
    ?.filter(filterOffice)
    ?.map((x) => {
      return {
        name: x.businessOperation,
        id: x.officeName,
        value: x.officeCode,
        label: x.officeCode,
        phone: x.phone,
        branchManager: x.branchManagerName,
        branchEmpNumber: x.branchEmpNumber,
        brancManagerId: x.branchManagerId,
        officeAcquisition: x.acquisitionName,
        country: x.country,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const sfEmpStatusOpt = useEmployeeStatus()
    ?.filter(
      (x) =>
        x.isActive &&
        (isMexico ? x.usedInMexico === true : x.usedInMexico !== true),
    )
    .map((x) => {
      return { value: x.employeeStatusId, label: x.employeeStatusName };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const sfEmpTypeOpt = useEmployeeType()
    ?.filter((x) => {
      if (!x.isActive) return false;

      // Mexico user
      if (isMexico) {
        return x.usedInMexico === true;
      }

      // Confie only users
      if (user?.isNonFranchise && !user?.isFranchise) {
        return x.usedInMexico !== true && x.employeeTypeName !== "Franchise";
      }

      // Franchise-only users
      if (user?.isFranchise && !user?.isNonFranchise) {
        return x.employeeTypeName === "Franchise";
      }

      // Show all non-Mexico options
      return x.usedInMexico !== true;
    })
    .map((x) => {
      return {
        value: x.employeeTypeId,
        label: x.employeeTypeName,
        empTypeid: x.employeeTypeId,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const sfEmpntTypeOpt = useEmploymentType()
    ?.filter(
      (x) =>
        x.isActive &&
        (isMexico ? x.usedInMexico === true : x.usedInMexico !== true),
    )
    .map((x) => {
      return { value: x.employmentTypeId, label: x.employmentTypeName };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: titleData, isLoading: titleLoading } = useEmployeeTitle();
  const sfEmpTitleOpt = titleData?.data
    .filter(
      (x) =>
        x.isActive &&
        (isMexico ? x.usedInMexico === true : x.usedInMexico !== true),
    )
    .map((x) => {
      return {
        value: x.employeeTitleId,
        label: x.employeeTitleName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: departmentData, isLoading: departmentLoading } =
    useEmployeeDepartment();
  const sfEmpDepartmentOpt = departmentData
    ?.filter(
      (x) =>
        x.isActive &&
        (isMexico ? x.usedInMexico === true : x.usedInMexico !== true),
    )
    .map((x) => {
      return {
        value: x.employeeDepartmentId,
        label: x.employeeDepartmentName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: terminationStatusData } = useEmployeeTerminationStatus();
  const EmpTerminationStatusOpt = terminationStatusData
    ?.filter((x) => x.isActive === 1)
    .map((x) => {
      return {
        value: x.id,
        label: x.statusName,
      };
    });
  const { data: terminationReasonData } = useEmployeeTerminationReason();

  const EmpTerminationReasonOpt = terminationReasonData
    ?.filter((x) => x.isActive === 1)
    .map((x) => {
      return {
        value: x.id,
        label: x.reason,
      };
    });

  const { data: emailDomains, isLoading: isGetEmailDomainsLoading } =
    useGetEmailDomains();

  const emailDomainsOpt = emailDomains?.data
    .filter((x) => x.isActive === true)
    .map((x) => {
      return { value: x.emailDomainId, label: x.emailDomainName };
    })
    ?.sort((a, b) => a.label?.trim().localeCompare(b.label?.trim()));

  const { data: empReportsToData } = useGetReportsTo();
  const empReportsTo = empReportsToData?.data
    ?.filter((x) => x.isActive === true)
    .map((y) => {
      return {
        id: y.reportsToId,
        label: y.employeeTitle,
      };
    });

  const { data: acquisitionNames, isLoading: acquisitionNameFetch } =
    useGetAcquisitionName();

  const acquisitionNameOpt = acquisitionNames
    ?.filter((x) =>
      isMexico ? x.usedInMexico === true : x.usedInMexico !== true,
    )
    .map((x) => {
      return {
        label: x.acquisitionName,
        value: x.acquisitionId,
        usedInMexico: x.usedInMexico,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: homeCompanyData, isLoading: homeCompanyLoading } =
    useGetHomeCompanies();
  const homeCompanyOpts = homeCompanyData
    ?.map((x) => {
      return {
        value: x.homeCompanyId,
        label: x.homeCompanyName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: separationTypeData, isLoading: separationTypeLoading } =
    useGetSeparationType();
  const separationTypeOpts = separationTypeData
    ?.map((x) => {
      return {
        value: x.separationTypeId,
        label: x.separationTypeName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: separationReasonData, isLoading: separationReasonLoading } =
    useGetSeparationReason();
  const reasonOpts = separationReasonData
    ?.map((x) => {
      return {
        value: x.separationReasonId,
        label: x.separationReasonName,
        separationTypeId: x.separationTypeId,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const separationTypeId = watch("separationTypeId");
  const separationReasonOpts =
    separationTypeId === 1 || separationTypeId === 2
      ? reasonOpts?.filter((opt) => opt.separationTypeId === separationTypeId)
      : reasonOpts;

  const { data: rehirableData, isLoading: rehirableLoading } =
    useGetRehirable();
  const rehirableOpts = rehirableData?.map((x) => {
    return {
      value: x.rehirableId,
      label: x.rehirableName,
    };
  });

  const managers = [
    "Team Manager",
    "District Manager",
    "Director",
    "Vice President",
    "Senior Vice President",
    "Executive",
  ];

  const isSplc = watch("isSplc");

  useEffect(() => {
    if (!isSplc) {
      // Clear ReportsToHierarchyId when IsSplc is unchecked
      setValue("reportsToHierarchyId", null);
    }
  }, [isSplc, setValue]);

  const managerOptions = managers
    .filter(
      (level) =>
        !(isSplc && !watch("isHierarchyManager")) || level === "Team Manager",
    )
    .map((level) => ({
      value: level,
      label: level,
    }));

  const { data: hierarchyData, isLoading: isHierarchyOptsLoading } =
    useGetHierarchyOpts();

  const managerHierarchyMap = {
    "Team Manager": "Team",
    "District Manager": "District",
    Director: "State",
    "Vice President": "Market",
    "Senior Vice President": "Business Unit",
  };

  const selectedManager = watch("hierarchyLeadership");
  const selectedHierarchyType = managerHierarchyMap[selectedManager] || "Team";

  const filteredHierarchyOpts = hierarchyData?.filter((x) => {
    // Exclude the employee itself in the option
    const isSameEmployee =
      isEditState && x.employeeId === rowForEdit?.employeeId;

    if (!selectedHierarchyType) return !isSameEmployee;

    const matchesType = x.hierarchyTypeName === selectedHierarchyType;

    // Filter based on Employee Type
    const selectedEmployeeType = watch("employeeTypeName");
    const hasSelectedEmployeeType = !!selectedEmployeeType;
    const isFranchiseEmployee = selectedEmployeeType?.includes("Franchise");

    const franchiseOnly =
      user.isFranchise && !user.isNonFranchise && !user.isMexico;
    const countryFilter = franchiseOnly ? x.isMexico !== true : true;

    let franchiseFilter = true;
    if (!isMexico) {
      if (user.isFranchise && user.isNonFranchise) {
        if (hasSelectedEmployeeType) {
          franchiseFilter = isFranchiseEmployee
            ? x.isFranchise === true
            : x.isFranchise !== true;
        }
      } else if (user.isFranchise && !user.isNonFranchise) {
        franchiseFilter = x.isFranchise === true;
      } else if (!user.isFranchise && user.isNonFranchise) {
        franchiseFilter = x.isFranchise !== true;
      }
    }

    return matchesType && !isSameEmployee && countryFilter && franchiseFilter;
  });

  const hierarchyOpts = filteredHierarchyOpts
    ?.map((x) => {
      return {
        ...x,
        label: x.hierarchyName
          ? `${x.hierarchyName} - ${x.employeeName}`
          : x.employeeName,
        value: x.hierarchyId,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const onReportsToChange = (data) => {
    if (data) {
      //reports to
      setValue("reportsToEmployeeId", data.employeeId);

      const employeeFieldMap = {
        //leadership
        teamManager: "teamManagerEmployeeName",
        districtManager: "districtManagerEmployeeName",
        director: "directorEmployeeName",
        vicePresident: "vicePresidentEmployeeName",
        seniorVicePresident: "seniorVicePresidentEmployeeName",

        //hierarchy names
        teamName: data.hierarchyTypeName === "Team" ? "teamName" : "",
        districtName: "districtHierarchyName",
        stateName: "stateHierarchyName",
        marketName: "marketHierarchyName",
        businessUnitName: "businessUnitHierarchyName",
      };

      Object.entries(employeeFieldMap).forEach(([formField, dataKey]) => {
        setValue(formField, data[dataKey] || "");
      });
    } else {
      const stringFields = [
        "teamManager",
        "districtManager",
        "director",
        "vicePresident",
        "seniorVicePresident",
        "teamName",
        "districtName",
        "stateName",
        "marketName",
        "businessUnitName",
      ];

      const nullFields = ["reportsToEmployeeId"];

      stringFields.forEach((field) => setValue(field, ""));
      nullFields.forEach((field) => setValue(field, null));
    }
  };

  //For dynamic hierarchy fields
  const selectedLeadership = watch("hierarchyLeadership");
  const hierarchyLevels = [
    "Team Manager",
    "District Manager",
    "Director",
    "Vice President",
    "Senior Vice President",
    "Executive",
  ];
  const selectedIndex = hierarchyLevels.indexOf(selectedLeadership);

  const leadership =
    selectedIndex === -1
      ? hierarchyLevels.slice(1)
      : hierarchyLevels.slice(selectedIndex + 1);
  const visibleRoles =
    selectedLeadership && selectedIndex >= 0
      ? hierarchyLevels.slice(selectedIndex)
      : hierarchyLevels;

  useEffect(() => {
    const officeCode = watch("officecode");
    const selectedOffice = sfOfficeNameOpt?.find(
      (office) => office.id === officeCode,
    );

    setSelectedOfficeStatus(selectedOffice?.officeStatus || "");
  }, [watch, sfOfficeNameOpt]);

  const terminatedStatus = watch("status") === "Terminated";

  const [searchBox, setSearchBox] = useState(null);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: user?.googleApiKey ? user?.googleApiKey : "",
    libraries,
  });

  const onLoad = (ref) => {
    setSearchBox(ref);
  };

  const clearAddress = () => {
    setValue("state", "");
    setValue("city", "");
    setValue("zipcode", "");
    setValue("street", "");
    setValue("latitude", "");
    setValue("longitude", "");
    setValue("geolocation", "");
    setValue("shippingLocation", "");
    setValue("street_number", "");
    setValue("route", "");
    setValue("stateCode", "");
    setValue("employeeState", "");
    setValue("address", "");
    setValue("country", "");
    setValue("countryCode", "");
  };

  const OnPlacesChanged = () => {
    const place = searchBox?.getPlace();
    clearAddress();

    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();

    if (typeof lat === "number" && typeof lng === "number") {
      setValue("latitude", lat);
      setValue("longitude", lng);
      setValue("geolocation", `${lat}, ${lng}`);
    }

    if (place.formatted_address) {
      setValue("shippingLocation", place.formatted_address);
    } else if (place.name) {
      setValue("shippingLocation", place.name);
    }

    const components = place.address_components || [];

    let streetNumber = "";
    let route = "";

    components.forEach((component) => {
      const type = component.types?.[0];

      if (!type) return;

      switch (type) {
        case "street_number":
          if (component.long_name) {
            streetNumber = component.long_name;
          }
          break;

        case "route":
          if (component.long_name) {
            route = component.long_name;
          }
          break;

        case "locality":
          if (component.long_name) {
            setValue("city", component.long_name);
          }
          break;

        case "administrative_area_level_1":
          if (component.long_name || component.short_name) {
            const stateCode = getStateCode(
              component.long_name,
              component.short_name,
            );

            setValue("employeeState", component.long_name || "");
            setValue("stateCode", stateCode);
          }
          break;

        case "postal_code":
          if (component.long_name) {
            setValue("zipcode", component.long_name);
          }
          break;

        case "country":
          if (component.long_name || component.short_name) {
            setValue("country", component.long_name || "");
            setValue("countryCode", component.short_name || "");
          }
          break;

        default:
          break;
      }
    });

    if (streetNumber || route) {
      setValue("address", `${streetNumber} ${route}`.trim());
    }
  };

  const resetDisplayKeyboard = () => {
    setDisplayKeyboard({
      firstName: false,
      middleName: false,
      lastName: false,
      preferredName: false,
      upn: false,
      suiteNumber: false,
      email: false,
    });
  };

  useEffect(() => {
    resetDisplayKeyboard();
  }, []);

  // reset value
  useEffect(() => {
    if (watch("employeeTypeName")?.includes("Franchise") || isMexico) {
      const { premierHome, priorityHome, healthcare } = getValues();

      if (premierHome || priorityHome || healthcare) {
        setValue("isPremierHome", false);
        setValue("isPriorityHome", false);
        setValue("isHealthcare", false);
      }
    }
  }, [watch("employeeTypeName"), isMexico]);

  const onSubmit = async (data) => {
    if (
      (rehire || data.status?.toLowerCase() === "rehire") &&
      !data.rehireDate
    ) {
      setError("rehireDate", {
        message: "Required Field",
      });
      return;
    }

    // Email validation can be improved with zod
    const validEmail = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(
      data.email,
    );
    const hasEmail = data.email?.trim();

    if (!validEmail && hasEmail) {
      setError("email", {
        message: "Invalid email address",
      });
      return;
    }

    //Normalize data before comparing
    const normalize = (obj) =>
      JSON.parse(
        JSON.stringify(obj, (_, value) => (value === undefined ? "" : value)),
      );

    if (
      isEditState &&
      JSON.stringify(normalize(data)) === JSON.stringify(normalize(rowForEdit))
    ) {
      toast.fire("Saving Failed", "No Changes have been made", "warning");
      setLocationChanged(false);
      setTitleChanged(false);
    } else if (
      selectedOfficeStatus === "Closed" &&
      watch("status")?.toLowerCase() !== "terminated" &&
      !watch("employeeTypeName")?.toLowerCase()?.includes("franchise")
    ) {
      toast.fire(
        "Saving Failed",
        "Office is Closed. Please change the Office",
        "warning",
      );
    } else {
      resetDisplayKeyboard();

      const acquisitionName = acquisitionNameOpt?.find(
        (x) => x.value === data?.acquisitionId,
      )?.label;

      const middleNameT = data.middlename?.trim();
      const middleName = middleNameT ? ` ${middleNameT} ` : " ";
      const fullName =
        data.firstname?.trim() + middleName + data.lastname?.trim();

      setNewData({
        ...data,
        mobile: data.mobile?.trim() || null,
        status: data.status,
        startdate: data.startdate === "" ? null : data.startdate,
        changeeffectivedate:
          data.changeeffectivedate === "" ? null : data.changeeffectivedate,
        rehireDate: data.rehireDate === "" ? null : data.rehireDate,
        terminationdate:
          data.terminationdate === "" ? null : data.terminationdate,
        onLeaveEffectiveDate:
          data.onLeaveEffectiveDate === "" ? null : data.onLeaveEffectiveDate,
        leavereturndate:
          data.leavereturndate === "" ? null : data.leavereturndate,
        reportstotermdate:
          data.reportstotermdate === "" ? null : data.reportstotermdate,
        createdby: data.createdby,
        reportstoemployeenumber: data.reportstoemployeenumber,
        address: data.address,
        city: data.city,
        stateCode: data.stateCode,
        state: data.employeeState,
        postalCode: data.postalCode,
        zipcode: data.zipcode,
        country: data.country,
        countryCode: data.countryCode,
        latitude: data.latitude,
        longitude: data.longitude,
        geolocation: data.geolocation,
        shippingLocation: data.geolocation,
        county: data.county,
        acquisitionName: acquisitionName,
        fullname: data.isMexico ? handleReplaceSpecialChar(fullName) : fullName,
        firstname: data.isMexico
          ? handleReplaceSpecialChar(data.firstname?.trim())
          : data.firstname?.trim(),
        lastname: data.isMexico
          ? handleReplaceSpecialChar(data.lastname?.trim())
          : data.lastname?.trim(),
        preferredName: data.isMexico
          ? handleReplaceSpecialChar(data.preferredName?.trim())
          : data.preferredName?.trim(),
        middlename: data.isMexico
          ? handleReplaceSpecialChar(data.middlename?.trim())
          : data.middlename?.trim(),
        upn: data.isMexico
          ? handleReplaceSpecialChar(data.upn?.trim())
          : data.upn?.trim(),
        suiteNumber: data.isMexico
          ? handleReplaceSpecialChar(data.suiteNumber?.trim() || "")?.trim() ||
            null
          : data.suiteNumber?.trim() || null,
        email: data.isMexico
          ? handleReplaceSpecialChar(data.email?.trim())
          : data.email?.trim(),
        tressFirstName: data.isMexico ? data.firstname?.trim() : "",
        tressFullname: data.isMexico ? fullName : "",
        tressLastname: data.isMexico ? data.lastname?.trim() : "",
        tressMiddlename: data.isMexico ? data.middlename?.trim() : "",
        tressPreferredName: data.isMexico ? data.preferredName?.trim() : "",
        tressSuiteNumber: data.isMexico ? data.suiteNumber?.trim() : "",
        tressEmail: data.isMexico ? data.email?.trim() : "",
        tressUpn: data.isMexico ? data.upn?.trim() : "",
        isTressEmployee: data.isMexico,
      });

      //Check for duplicate employee name before saving
      const duplicateData = await getEmployeesByFullName(
        fullName,
        data.employeeId,
      );

      if (duplicateData) {
        setDuplicateModal(true);
      } else {
        setSaveModal(true);
      }
    }
  };
  const { data: bilingualField } = useEmployeeBilingual();
  const employeeBilingualOpt = bilingualField?.data
    ?.map((x) => {
      return {
        value: x.bilingualId,
        label: x.bilingualName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));
  const requiredMirrorEmployee =
    watch("status")?.toLowerCase()?.includes("new hire") &&
    watch("employeeTypeName")?.toLowerCase()?.includes("support");

  const addressRequired = watch("officeName")
    ?.toLowerCase()
    ?.includes("work from home");
  const requiredBilingual =
    watch("employeeTypeName")?.toLowerCase() === "sales";

  const requiredTerminationReason = watch("terminationStatus") === 3; //Not Approved Status

  const notTerminated = watch("status")?.toLowerCase() !== "terminated";
  const confie = watch("employeeTypeName")?.toLowerCase() !== "franchise";
  const sfAsosciateToOpt = offices
    ?.filter((x) => !x.officeName?.toLowerCase()?.includes("work from home"))
    .map((x) => {
      return {
        id: x.officeId,
        value: x.officeId,
        label: x.officeName,
      };
    });
  const middleName = watch("middlename")
    ? " " + watch("middlename") + " "
    : " ";
  const fullName = watch("firstname") + middleName + watch("lastname");
  const empNumber = watch("employeenumber");

  const OfficeAutoReportsTo = (e) => {
    //Skip auto populating reports to
    if (isSplc) return;

    const titleId = watch("title");
    const emp = reportsToOptions?.filter((x) => x.id === e.brancManagerId);
    if (titleId !== undefined) {
      const validTitle = sfEmpTitleOpt?.filter((x) => x.value === titleId);

      const matchedTitle = empReportsTo?.find((x) =>
        x.label.includes(validTitle[0]?.label),
      );
      if (confie && matchedTitle !== undefined) {
        if (
          isAddState &&
          (fullName === null || fullName === "") &&
          (empNumber === null || empNumber === "")
        ) {
          setValue("reportsToEmployeeId", e ? e?.branchManagerId : null);
        } else if (
          e.branchManager?.toLowerCase() === fullName?.toLowerCase() &&
          e.branchEmpNumber === empNumber
        ) {
          setValue("reportsToEmployeeId", null);
        } else {
          if (emp?.length > 0) {
            setValue("reportsToEmployeeId", emp[0]?.id);
          } else {
            setValue("reportsToEmployeeId", null);
          }
        }
      } else {
        setValue("reportsToEmployeeId", null);
      }
    } else {
      setValue("reportsToEmployeeId", null);
    }
  };

  const titleAutoReportsTo = (e) => {
    //Skip auto populating reports to
    if (isSplc) return;

    const validTitle = sfEmpTitleOpt?.filter((x) => x.value === e?.value);
    const matchedTitle = empReportsTo?.find((x) =>
      x.label.includes(validTitle[0]?.label),
    );
    const officeCode = watch("officecode");
    if (confie && matchedTitle !== undefined) {
      if (officeCode !== undefined) {
        const name = sfOfficeNameOpt?.filter((x) => x.id === officeCode);
        const emp = reportsToOptions?.filter(
          (x) => x.id === name[0]?.brancManagerId,
        );
        if (name[0]?.branchManager !== undefined) {
          if (
            isAddState &&
            (fullName === null || fullName === "") &&
            (empNumber === null || empNumber === "")
          ) {
            setValue("reportsToEmployeeId", name[0]?.brancManagerId);
          } else if (
            name[0]?.branchManager?.toLowerCase() === fullName?.toLowerCase() &&
            name[0]?.branchEmpNumber === empNumber
          ) {
            setValue("reportsToEmployeeId", null);
          } else {
            if (emp?.length > 0) {
              setValue("reportsToEmployeeId", emp[0]?.id);
            } else {
              setValue("reportsToEmployeeId", null);
            }
          }
        }
      }
    } else {
      setValue("reportsToEmployeeId", null);
    }
  };

  const handleSave = () => {
    if (isSubmittingRef.current) return; // hard stop when already submitted
    isSubmittingRef.current = true;
    setIsConfirming(true);
    setIsSaving(true);

    const finalize = () => {
      isSubmittingRef.current = false;
      setIsConfirming(false);
    };

    if (isAddState) {
      AddEmployee(newData, {
        onSettled: () => {
          finalize();
          setSaveModal(false);
          setDuplicateModal(false);
        },
      });
    } else {
      const invalidateEmployeeQueries = () => {
        queryClient.invalidateQueries("GetAllEmployees");
        queryClient.invalidateQueries({
          queryKey: ["GetEmployeesByEmployeeNumber"],
        });
        queryClient.invalidateQueries("GetEmployees");
      };

      EditEmployee(newData, {
        onSuccess: (res) => {
          if (res.data === "duplicateEmpNumber") {
            toast.fire(
              "Updating Employee failed",
              "Employee Number already exists",
              "error",
            );
            setIsSaving(false);
          } else if (res.data === "duplicateUPN") {
            toast.fire(
              "Updating Employee failed",
              "Employee UPN already exists",
              "error",
            );
            setIsSaving(false);
          } else if (res.status === 200 || res.status === 201) {
            toast.fire("Success", "Employee updated successfully", "success");
            sendEmail(res.data, {
              onSuccess: (res) => {
                if (res) {
                  if (res.status === 400) {
                    toast.fire(
                      "Success",
                      "Employee Updated Successfully, but failed in sending email",
                      "warning",
                    );
                  } else {
                    toast.fire(
                      "Success",
                      "Employee updated successfully",
                      "success",
                    );
                  }
                }
              },
            });

            if (!!res.data.hierarchyData) {
              sendHierarchyEmail(res.data.hierarchyData, {});
            }

            invalidateEmployeeQueries();

            setEditState(false);
            setIsSaving(false);
            setRowToEdit([]);
            onSaveSuccess();

            // FIX: Redirect back to office ONLY after successful save
            if (showFromOffice) {
              localStorage.removeItem("isEditFromOffice");
              onClickCancelShow();
            }
          } else {
            toast.fire(
              "Updating Employee failed",
              "Server Error: Cannot update new Employee",
              "error",
            );
            setIsSaving(false);
          }
          invalidateEmployeeQueries();
          finalize();
          setSaveModal(false);
          setDuplicateModal(false);
        },
        onError: (error) => {
          setIsSaving(false);
          if (
            error.response.data.message === "Failed sending of email" ||
            error.response.data.message ===
              "Failed inserting of notification" ||
            error.response.data.message === "Email Not Sent"
          ) {
            toast.fire(
              "Success",
              "Employee updated successfully, but " +
                error.response.data.message,
              "warning",
            );
            invalidateEmployeeQueries();
          } else {
            toast.fire(
              "Adding new Employee failed",
              "Server Error: Cannot add new Employee",
              "error",
            );
            setIsSaving(false);
          }
          invalidateEmployeeQueries();
          finalize();
        },
      });
    }
  };

  const handleCancel = () => {
    if (showFromOffice) {
      onClickCancelShow();
    }
    reset();
    clearErrors();
    setRowToEdit([]);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setOnClose(true);
    localStorage.removeItem("isEditFromOffice");
  };

  const handleFieldOnChange = (e, fieldName) => {
    switch (fieldName) {
      case "status":
        if (e?.toLowerCase() === "on leave") {
          setOnLeave(true);
          setWasTerminated(false);
          setFromLeave(false);
          setRehire(false);
          setValue("terminationdate", null);
        } else if (e?.toLowerCase() === "active") {
          if (rowForEdit.status?.toLowerCase() === "on leave") {
            setFromLeave(true);
          }
          if (
            rowForEdit.status?.toLowerCase() === "terminated" &&
            watch("terminationdate")
          ) {
            setRehire(true);
            setWasTerminated(true);
          } else {
            setWasTerminated(false);
          }
        } else if (e?.toLowerCase() === "terminated") {
          setWasTerminated(true);
          setOnLeave(false);
          setFromLeave(false);
          setRehire(false);
          setValue(
            "terminationStatus",
            EmpTerminationStatusOpt.filter(
              (x) => x.label?.toLowerCase() === "pending",
            )[0].value,
          );
          setValue("terminationdate", rowForEdit.terminationdate);
        } else if (e?.toLowerCase() === "rehire") {
          setRehire(true);
          setOnLeave(false);
          setWasTerminated(true);
          setFromLeave(false);
        } else {
          setOnLeave(false);
          setWasTerminated(false);
          setFromLeave(false);
          setRehire(false);
        }
        break;
      case "terminationStatus":
        if (e?.toLowerCase() === "not approved") {
          //Not Approved
          const status = sfEmpStatusOpt.filter(
            (x) => x.label?.toLowerCase() === "active",
          );
          setValue("status", status[0]?.label);
          setValue("statusId", status[0]?.value);
          setValue("terminationdate", null);
          setWasTerminated(false);
        } else {
          const status = sfEmpStatusOpt.filter(
            (x) => x.label?.toLowerCase() === "terminated",
          );
          setValue("terminationReason", null);
          setValue("status", status[0]?.label);
          setValue("statusId", status[0]?.value);
          // persist termination date data
          setValue("terminationdate", rowForEdit?.terminationdate);
          setWasTerminated(true);
          clearErrors();
        }
        break;
      default:
    }
  };
  var minRehireDate = watch("terminationdate")
    ? new Date(
        new Date(watch("terminationdate")).setDate(
          new Date(watch("terminationdate")).getDate() + 1,
        ),
      )
    : null;
  const keyboardOpts = [
    { value: "á", normalValue: "a", label: "á", isCapital: false },
    { value: "Ý", normalValue: "A", label: "Ý", isCapital: true },
    { value: "é", normalValue: "e", label: "é", isCapital: false },
    { value: "É", normalValue: "E", label: "É", isCapital: true },
    { value: "í", normalValue: "i", label: "í", isCapital: false },
    { value: "Ý", normalValue: "I", label: "Ý", isCapital: true },
    {
      value: "del",
      label: <BackspaceIcon />,
      normalValue: "",
      isCapital: null,
    },
    { value: "ó", normalValue: "o", label: "ó", isCapital: false },
    { value: "Ó", normalValue: "O", label: "Ó", isCapital: true },
    { value: "ú", normalValue: "u", label: "ú", isCapital: false },
    { value: "Ú", normalValue: "U", label: "Ú", isCapital: true },
    { value: "ç", normalValue: "c", label: "ç", isCapital: false },
    { value: "Ç", normalValue: "C", label: "Ç", isCapital: true },
    { value: "ĝ", normalValue: "d", label: "ĝ", isCapital: false },
    { value: "Ď", normalValue: "D", label: "Ď", isCapital: true },
    { value: "ý", normalValue: "y", label: "ý", isCapital: false },
    { value: "Ý", normalValue: "Y", label: "Ý", isCapital: true },
    { value: "ñ", normalValue: "n", label: "ñ", isCapital: false },
    { value: "Ñ", normalValue: "N", label: "Ñ", isCapital: true },
    { value: "ì", normalValue: "i", label: "ì", isCapital: false },
    { value: "Ì", normalValue: "I", label: "Ì", isCapital: true },
    { value: "¿", normalValue: "?", label: "¿", isCapital: null },
    {
      value: "capital",
      label: <KeyboardCapslock />,
      normalValue: "",
      isCapital: null,
    },
    { value: "¥", label: "¥", normalValue: "", isCapital: null },
    { value: "ß", label: "ß", normalValue: "", isCapital: null },
    { value: "£", label: "£", normalValue: "", isCapital: null },
  ];

  const isValidEmployeeName = (name) => {
    const regex =
      /^[A-Za-zÁáÉéÍíÓóÚúÝýÑñÌìÇç]+(?:[- ][A-Za-zÁáÉéÍíÓóÚúÝýÑñÌìÇç]+)*$/;
    const trimmedName = name.trim();

    if (trimmedName === "") {
      return true;
    }

    return regex.test(name.trim());
  };

  const handleKeyboardToggle = (fieldname) => {
    showIsKeyboardCaps(false);
    setDisplayKeyboard((prevState) => ({
      firstName: fieldname === "firstName" ? !prevState.firstName : false,
      middleName: fieldname === "middleName" ? !prevState.middleName : false,
      lastName: fieldname === "lastName" ? !prevState.lastName : false,
      preferredName:
        fieldname === "preferredName" ? !prevState.preferredName : false,
      upn: fieldname === "upn" ? !prevState.upn : false,
      suiteNumber: fieldname === "suiteNumber" ? !prevState.suiteNumber : false,
      email: fieldname === "email" ? !prevState.email : false,
    }));
  };

  const handleCloseKeyboard = () => {
    showIsKeyboardCaps(false);
    setDisplayKeyboard({
      firstName: false,
      middleName: false,
      lastName: false,
      upn: false,
      suiteNumber: false,
      email: false,
    });
  };

  function handleReplaceSpecialChar(inputString) {
    const regex = new RegExp(keyboardOpts?.map((x) => x.value).join("|"), "g");
    return inputString?.replace(regex, (match) => {
      const map = keyboardOpts?.find((x) => x.value === match);
      return map ? map.normalValue : match;
    });
  }

  const handleKeyboardChange = (fieldName, event) => {
    const value = event.currentTarget.getAttribute("data-value") || "";
    const currentValue = watch(fieldName) ?? "";

    // Get the input element
    const input = document.querySelector(`input[name="${fieldName}"]`);
    if (!input) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;

    if (value) {
      let updatedValue = currentValue;

      if (value?.trim() === "del") {
        updatedValue =
          currentValue?.slice(0, start - 1) + currentValue?.slice(end);
        setTimeout(() => input.setSelectionRange(start - 1, start - 1), 0);
      } else if (value?.trim() === "capital") {
        showIsKeyboardCaps(!isKeyboardCaps);
        return;
      } else {
        // Insert character at the cursor position
        updatedValue =
          currentValue.substring(0, start) +
          value +
          currentValue.substring(end);

        // Move cursor position after inserted character
        setTimeout(
          () =>
            input.setSelectionRange(start + value.length, start + value.length),
          0,
        );
      }
      setValue(fieldName, updatedValue);
    }
  };

  return (
    <div>
      {sfOfficeNameOpt &&
      sfOfficeCodeOpt &&
      sfEmpStatusOpt &&
      sfEmpntTypeOpt &&
      sfAsosciateToOpt &&
      rowForEdit ? (
        <div>
          <div className="p-3 space-y-2 border-l-2 border-primary box-shadow mt-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg  pb-1">
                <div className="ml-2 font-bold text-primary text-md">
                  {isAddState ? "Add Employee" : "Edit Employee"}
                </div>

                <div className="flex justify-between gap-2 mr-2">
                  <SubmitHookForm
                    type="submit"
                    disabled={isSaving}
                    buttonText={isSaving ? "Saving.." : "Save"}
                    className={undefined}
                    onClick={undefined}
                  />
                  <CancelButton onClick={() => handleCancel()} />
                </div>
              </div>
              <div className="mx-2 border-b-2 border-secondary"></div>

              <div className="bg-bgDB">
                <div className="flex justify-between items-center my-3 mx-3">
                  <div className="text-xs text-secondary italic font-normal">
                    <b> Note:</b> All fields with * are required to fill.
                  </div>

                  <div className="flex items-center gap-3">
                    {user.isNonFranchise &&
                      !watch("employeeTypeName")?.includes("Franchise") &&
                      !isMexico && (
                        <>
                          <CheckboxInlineHookForm
                            name="isHealthcare"
                            label="Healthcare"
                            register={register}
                            disabled={watch("isPriorityHome")}
                          />
                          <CheckboxInlineHookForm
                            name="isPremierHome"
                            label="Premier Home"
                            register={register}
                            disabled={watch("isPriorityHome")}
                          />
                          <CheckboxInlineHookForm
                            name="isPriorityHome"
                            label="Priority Home"
                            register={register}
                            disabled={
                              watch("isPremierHome") || watch("isHealthcare")
                            }
                          />
                        </>
                      )}

                    <CheckboxInlineHookForm
                      name="isMexico"
                      label="IsMexico"
                      register={register}
                      disabled={
                        isAddState ? !(hasUSAdd && mexicoAdd) : isEditState
                      }
                      onClick={() => {
                        reset();
                        setValue("isMexico", !isMexico);
                      }}
                    />
                    <CheckboxInlineHookForm
                      name="isSplc"
                      label="IsSplc"
                      register={register}
                    />
                  </div>
                </div>
                {userPermissions.includes(viewContactPermission) && (
                  <div name="contact-details">
                    <div className="m-2 bg-white box-shadow rounded-lg h-full">
                      <div className="text-quinary font-bold font-sans py-2 px-4">
                        Name Details
                      </div>
                      <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 lg:gap-12">
                        <div className="space-y-1 w-full pb-2">
                          <div className="space-y-1">
                            {isEditState && (
                              <div>
                                <TextInputHookFormDisabled
                                  label="Record ID"
                                  type="text"
                                  value={watch("employeeId")}
                                />
                              </div>
                            )}
                            <div>
                              <Controller
                                control={control}
                                name="salutation"
                                rules={{ required: false }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Salutation"
                                    className=""
                                    options={salutationOptions}
                                    isMulti={false}
                                    isDisabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                    }}
                                    value={field.value}
                                  />
                                )}
                              />
                            </div>
                            <div>
                              <TextInputHookFormWithKB
                                name={"firstname"}
                                label="First Name"
                                type="text"
                                placeholder="First Name"
                                className={
                                  errors.firstname &&
                                  "border-2 border-red-500 col-span-2"
                                }
                                register={register("firstname", {
                                  required: true,
                                  validate: (value) =>
                                    value?.trim().length > 0 &&
                                    isValidEmployeeName(value),
                                })}
                                required={true}
                                isTressEmployee={isMexico}
                                disabled={
                                  permissionContactEdit
                                    ? !permissionContactEdit
                                    : permissionContactAdd
                                      ? !permissionContactAdd
                                      : true
                                }
                                showKeyboardRight={true}
                                keyboardOptions={
                                  keyboardOpts?.filter(
                                    (x) =>
                                      x.isCapital === isKeyboardCaps ||
                                      x.isCapital === null,
                                  ) ?? []
                                }
                                isKeyboardCaps={isKeyboardCaps}
                                keyboardOnclick={(e) => {
                                  handleKeyboardChange("firstname", e);
                                }}
                                isKeyboardOpen={displayKeyboard.firstName}
                                onClickShowKeyboard={(e) => {
                                  handleKeyboardToggle("firstName");
                                }}
                                onClickCloseKeyboard={handleCloseKeyboard}
                              />
                            </div>
                            <div>
                              <Controller
                                control={control}
                                name="preferredName"
                                render={({ field }) => (
                                  <TextInputHookFormWithKB
                                    name="preferredName"
                                    label="Preferred Name"
                                    type="text"
                                    placeholder="Preferred Name"
                                    register={register("preferredName")}
                                    keyboardOptions={
                                      keyboardOpts?.filter(
                                        (x) =>
                                          x.isCapital === isKeyboardCaps ||
                                          x.isCapital === null,
                                      ) ?? []
                                    }
                                    isTressEmployee={isMexico}
                                    showKeyboardRight={false}
                                    isKeyboardCaps={isKeyboardCaps}
                                    keyboardOnclick={(e) => {
                                      handleKeyboardChange("preferredName", e);
                                    }}
                                    isKeyboardOpen={
                                      displayKeyboard.preferredName
                                    }
                                    onClickShowKeyboard={(e) => {
                                      handleKeyboardToggle("preferredName");
                                    }}
                                    onClickCloseKeyboard={handleCloseKeyboard}
                                  />
                                )}
                              />
                            </div>
                            <div>
                              <TextInputHookFormWithKB
                                name="upn"
                                label="UPN"
                                type="text"
                                placeholder="Upn"
                                className={
                                  errors.upn &&
                                  errors.upn.message &&
                                  "border-2 border-red-500 col-span-2"
                                }
                                register={register("upn", {})}
                                disabled={
                                  permissionContactEdit
                                    ? !permissionContactEdit
                                    : permissionContactAdd
                                      ? !permissionContactAdd
                                      : true
                                }
                                keyboardOptions={
                                  keyboardOpts?.filter(
                                    (x) =>
                                      x.isCapital === isKeyboardCaps ||
                                      x.isCapital === null,
                                  ) ?? []
                                }
                                isTressEmployee={isMexico}
                                showKeyboardRight={true}
                                isKeyboardCaps={isKeyboardCaps}
                                keyboardOnclick={(e) => {
                                  handleKeyboardChange("upn", e);
                                }}
                                isKeyboardOpen={displayKeyboard.upn}
                                onClickShowKeyboard={(e) => {
                                  handleKeyboardToggle("upn");
                                }}
                                onClickCloseKeyboard={handleCloseKeyboard}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1 w-full pb-2">
                          <div className="space-y-1">
                            <div className="space-y-1">
                              <TextInputHookFormWithKB
                                name="middlename"
                                label="Middle Name"
                                type="text"
                                className={
                                  errors.middlename &&
                                  errors.middlename.message &&
                                  "border-2 border-red-500 "
                                }
                                placeholder="Middle Name"
                                register={register("middlename", {
                                  validate: (value) =>
                                    isValidEmployeeName(value ?? ""),
                                })}
                                disabled={
                                  permissionContactEdit
                                    ? !permissionContactEdit
                                    : permissionContactAdd
                                      ? !permissionContactAdd
                                      : true
                                }
                                keyboardOptions={
                                  keyboardOpts?.filter(
                                    (x) =>
                                      x.isCapital === isKeyboardCaps ||
                                      x.isCapital === null,
                                  ) ?? []
                                }
                                isTressEmployee={isMexico}
                                showKeyboardRight={false}
                                isKeyboardCaps={isKeyboardCaps}
                                keyboardOnclick={(e) => {
                                  handleKeyboardChange("middlename", e);
                                }}
                                isKeyboardOpen={displayKeyboard.middleName}
                                onClickShowKeyboard={(e) => {
                                  handleKeyboardToggle("middleName");
                                }}
                                onClickCloseKeyboard={handleCloseKeyboard}
                              />
                            </div>
                            <div>
                              <TextInputHookFormWithKB
                                name="lastname"
                                label="Last Name"
                                type="text"
                                placeholder="Last Name"
                                className={
                                  errors.lastname &&
                                  "border-2 border-red-500 col-span-2"
                                }
                                register={register("lastname", {
                                  required: true,
                                  validate: (value) =>
                                    value?.trim().length > 0 &&
                                    isValidEmployeeName(value),
                                })}
                                required={true}
                                disabled={
                                  permissionContactEdit
                                    ? !permissionContactEdit
                                    : permissionContactAdd
                                      ? !permissionContactAdd
                                      : true
                                }
                                keyboardOptions={
                                  keyboardOpts?.filter(
                                    (x) =>
                                      x.isCapital === isKeyboardCaps ||
                                      x.isCapital === null,
                                  ) ?? []
                                }
                                isTressEmployee={isMexico}
                                showKeyboardRight={false}
                                isKeyboardCaps={isKeyboardCaps}
                                keyboardOnclick={(e) => {
                                  handleKeyboardChange("lastname", e);
                                }}
                                isKeyboardOpen={displayKeyboard.lastName}
                                onClickShowKeyboard={(e) => {
                                  handleKeyboardToggle("lastName");
                                }}
                                onClickCloseKeyboard={handleCloseKeyboard}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="m-2 p-2 bg-white box-shadow rounded-lg h-full">
                      <div className="text-quinary font-bold font-sans py-2 px-4">
                        Contact Details
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 px-4 lg:gap-12">
                        <div className="space-y-1 w-full pb-2">
                          <div className="">
                            <div className="">
                              {errors.email && (
                                <span className="flex text-red-500 text-xs italic justify-end -mt-4">
                                  {errors.email.message}
                                </span>
                              )}
                              <TextInputHookFormWithKB
                                name="email"
                                label="Email"
                                type="text"
                                placeholder="email@gmail.com"
                                register={register("email", {})}
                                title="Format: sample@email.com"
                                disabled={
                                  permissionContactEdit
                                    ? !permissionContactEdit
                                    : permissionContactAdd
                                      ? !permissionContactAdd
                                      : true
                                }
                                keyboardOptions={
                                  keyboardOpts?.filter(
                                    (x) =>
                                      x.isCapital === isKeyboardCaps ||
                                      x.isCapital === null,
                                  ) ?? []
                                }
                                isTressEmployee={isMexico}
                                showKeyboardRight={true}
                                isKeyboardCaps={isKeyboardCaps}
                                keyboardOnclick={(e) => {
                                  handleKeyboardChange("email", e);
                                }}
                                isKeyboardOpen={displayKeyboard.email}
                                onClickShowKeyboard={(e) => {
                                  handleKeyboardToggle("email");
                                }}
                                onClickCloseKeyboard={handleCloseKeyboard}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div>
                              {errors.mobile?.type === "maxLength" && (
                                <span className="flex text-red-500 text-xs italic justify-end">
                                  {errors.mobile.message}
                                </span>
                              )}{" "}
                              <TextInputLabelPhone
                                name="mobile"
                                label="Mobile"
                                type="tel"
                                pattern="\(\d{3}\) \d{3}-\d{4}"
                                title="Format: (000) 000-0000"
                                register={register("mobile", {})}
                                placeholder="(000) 000-0000"
                                onChange={(e) => {
                                  var x = e.target.value
                                    .replace(/\D/g, "")
                                    .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                                  e.target.value = !x[2]
                                    ? x[1]
                                    : "(" +
                                      x[1] +
                                      ") " +
                                      x[2] +
                                      (x[3] ? "-" + x[3] : "");
                                }}
                                disabled={
                                  permissionContactEdit
                                    ? !permissionContactEdit
                                    : permissionContactAdd
                                      ? !permissionContactAdd
                                      : true
                                }
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Controller
                              control={control}
                              name="department"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Department"
                                  options={sfEmpDepartmentOpt}
                                  className={
                                    errors.department &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  isMulti={false}
                                  isLoading={departmentLoading}
                                  required={true}
                                  disabled={
                                    permissionContactEdit
                                      ? !permissionContactEdit
                                      : permissionContactAdd
                                        ? !permissionContactAdd
                                        : true
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                  }}
                                  value={field?.value}
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name="homeCompanyId"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Home Company"
                                  options={homeCompanyOpts}
                                  className={
                                    errors.homeCompanyId &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  isMulti={false}
                                  isLoading={homeCompanyLoading}
                                  required={true}
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    if (
                                      e &&
                                      e?.label
                                        ?.toLowerCase()
                                        .includes("insureone")
                                    ) {
                                      setValue("isSplc", true);
                                    }
                                  }}
                                  value={field?.value}
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name="title"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Title"
                                  options={sfEmpTitleOpt}
                                  className={
                                    errors.title &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  isMulti={false}
                                  isLoading={titleLoading}
                                  required={true}
                                  disabled={
                                    permissionContactEdit
                                      ? !permissionContactEdit
                                      : permissionContactAdd
                                        ? !permissionContactAdd
                                        : true
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    setValue("changeeffectivedate", null);
                                    titleAutoReportsTo(e);
                                    isAddState
                                      ? e
                                        ? setTitleChanged(true)
                                        : setTitleChanged(false)
                                      : setTitleChanged(true);
                                  }}
                                  value={field?.value}
                                />
                              )}
                            />
                          </div>
                          <div className="space-y-1">
                            <Controller
                              control={control}
                              name="statusId"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  options={sfEmpStatusOpt}
                                  label="Status"
                                  className={
                                    errors.statusId &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  isMulti={false}
                                  required={true}
                                  disabled={
                                    permissionContactEdit
                                      ? !permissionContactEdit
                                      : permissionContactAdd
                                        ? !permissionContactAdd
                                        : true
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    setValue("status", e ? e.label : null);
                                    handleFieldOnChange(e?.label, "status");
                                    if (
                                      watch("status")?.toLowerCase() ===
                                      "on leave"
                                    ) {
                                      setValue("leavereturndate", null);
                                    }
                                  }}
                                  value={field.value}
                                />
                              )}
                            />
                            {isEditState && (
                              <TextInputLabelHookForm
                                name="previousemployeestatus"
                                label="Previous Employee Status"
                                type="text"
                                placeholder="Previous Employee Status"
                                register={register(
                                  "previousemployeestatus",
                                  {},
                                )}
                                disabled={true}
                              />
                            )}
                          </div>
                          <div className="space-y-1">
                            {(userPermissions.includes(
                              updateApprovedFieldReadOnly,
                            ) ||
                              userPermissions.includes(
                                updateApprovedField,
                              )) && (
                              <Controller
                                control={control}
                                name="terminationStatus"
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Update Approved"
                                    options={EmpTerminationStatusOpt}
                                    isDisabled={
                                      userPermissions.includes(
                                        updateApprovedField,
                                      )
                                        ? false
                                        : true
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                      handleFieldOnChange(
                                        e?.label,
                                        "terminationStatus",
                                      );
                                    }}
                                    value={field.value}
                                  />
                                )}
                              />
                            )}
                          </div>
                          <div
                            className="space-y-1"
                            id="div-termination-reason"
                          >
                            {(userPermissions.includes(updateApprovedField) ||
                              userPermissions.includes(
                                updateApprovedFieldReadOnly,
                              )) &&
                              requiredTerminationReason && (
                                <Controller
                                  control={control}
                                  name="terminationReason"
                                  rules={{
                                    required: requiredTerminationReason,
                                  }}
                                  render={({ field }) => (
                                    <SelectHookFormSingle
                                      label="Reason Denied"
                                      options={EmpTerminationReasonOpt}
                                      register={register("terminationReason", {
                                        required: {
                                          value: requiredTerminationReason,
                                          message:
                                            "Please specify the Termination Reason",
                                        },
                                      })}
                                      isMulti={false}
                                      required={requiredTerminationReason}
                                      className={
                                        errors.terminationReason &&
                                        "border-2 border-red-500 col-span-2"
                                      }
                                      isDisabled={
                                        userPermissions.includes(
                                          updateApprovedField,
                                        )
                                          ? false
                                          : true
                                      }
                                      onChange={(e) => {
                                        field.onChange(e ? e.value : null);
                                      }}
                                      value={field.value}
                                    />
                                  )}
                                />
                              )}
                            {errors.terminationReason?.message ===
                            "Please specify the Termination Reason" ? (
                              <>
                                <Tooltip
                                  title="Please specify the Termination Reason"
                                  placement="top-end"
                                  arrow
                                  disableTriggerFocus={true}
                                  open={true}
                                >
                                  <InformationCircleIcon
                                    data-for="div-termination-reason"
                                    className="absolute top top-3 right-0 h-5 w-5 -mr-5 text-red-500"
                                  />
                                </Tooltip>{" "}
                              </>
                            ) : (
                              ""
                            )}
                          </div>
                          <div className="space-y-1">
                            <Controller
                              control={control}
                              name="employeeTypeId"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Employee Type"
                                  options={sfEmpTypeOpt}
                                  className={
                                    errors.employeeTypeId &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  isMulti={false}
                                  required={true}
                                  disabled={
                                    permissionContactEdit
                                      ? !permissionContactEdit
                                      : permissionContactAdd
                                        ? !permissionContactAdd
                                        : true
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    handleFieldOnChange(
                                      e?.label,
                                      "EmployeeType",
                                    );
                                    setValue(
                                      "employeeTypeName",
                                      e ? e?.label : null,
                                    );
                                    if (!isSplc) {
                                      setValue("reportsToEmployeeId", null);
                                    }
                                    setValue("acquisitionName", null);
                                    setValue("officeName", null);
                                    setValue("officecode", null);
                                    setValue("mirroremployee", null);
                                    setValue("forwardemailsto", null);
                                    setValue("mirroremployeeemail", null);
                                    setValue("mirroremployeehrid", null);
                                    setValue("traininglocation", null);
                                    setValue("businessOperation", null);
                                    setValue("phone", null);
                                    setValue("associatedOfficeId", null);
                                    isAddState
                                      ? setLocationChanged(false)
                                      : setLocationChanged(true);
                                  }}
                                  value={field.value}
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name="employmentTypeId"
                              rules={{ required: false }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Employment Type"
                                  options={sfEmpntTypeOpt}
                                  isMulti={false}
                                  disabled={
                                    permissionContactEdit
                                      ? !permissionContactEdit
                                      : permissionContactAdd
                                        ? !permissionContactAdd
                                        : true
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                  }}
                                  value={field.value}
                                  isLoading={
                                    sfEmpntTypeOpt?.length > 0 ? false : true
                                  }
                                />
                              )}
                            />
                          </div>
                          {watch("employeeTypeName")?.includes("Franchise") ? (
                            <div className="space-y-1">
                              <Controller
                                control={control}
                                name="officecode"
                                rules={{ required: true }}
                                render={({ field }) => (
                                  <SelectHookFormMulti
                                    label="Office Name"
                                    options={sfOfficeNameOpt}
                                    isLoading={officeIsLoading}
                                    value={
                                      field.value
                                        ? field.value?.filter(
                                            (x) => x !== undefined,
                                          )
                                        : undefined
                                    }
                                    isDisabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                    className={
                                      errors.officeCode &&
                                      "border-2 border-red-500 col-span-2"
                                    }
                                    isRequired={true}
                                    onChange={(e) => {
                                      field.onChange(e);

                                      if (getValues("employeeTypeId") === 27) {
                                        if (
                                          getValues("businessOperation") !==
                                          null
                                        ) {
                                          setValue("businessOperation", null);
                                        } else {
                                          setValue(
                                            "businessOperation",
                                            e[0]?.name,
                                          );
                                        }
                                      } else {
                                        setValue("businessOperation", e?.name);
                                      }
                                      setValue("changeeffectivedate", null);

                                      isAddState
                                        ? e
                                          ? setLocationChanged(true)
                                          : setLocationChanged(false)
                                        : setLocationChanged(true);
                                    }}
                                  />
                                )}
                              />
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <Controller
                                control={control}
                                name="officeName"
                                rules={{ required: true }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Office Name"
                                    options={
                                      sfOfficeNameOpt ? sfOfficeNameOpt : []
                                    }
                                    isLoading={officeIsLoading}
                                    isMulti={false}
                                    disabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                    className={
                                      errors.officeName &&
                                      "border-2 border-red-500 col-span-2"
                                    }
                                    required={true}
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                      setValue("officecode", e ? e?.id : null);
                                      setValue("businessOperation", e?.name);
                                      setValue("changeeffectivedate", null);
                                      setValue("phone", e?.phone);
                                      if (
                                        userPermissions.includes(
                                          acquisitionName,
                                        )
                                      ) {
                                        setValue(
                                          "acquisitionName",
                                          e ? e?.officeAcquisition : "",
                                        );
                                      }
                                      if (e === null && !isSplc) {
                                        setValue("reportsToFullname", null);
                                        setValue(
                                          "reportsToEmployeeNumber",
                                          null,
                                        );
                                        setValue("reportsToEmployeeId", null);
                                      } else if (e.length > 0) {
                                        OfficeAutoReportsTo(e);
                                      }

                                      isAddState
                                        ? e
                                          ? setLocationChanged(true)
                                          : setLocationChanged(false)
                                        : setLocationChanged(true);
                                    }}
                                    value={field.value}
                                  />
                                )}
                              />
                              <Controller
                                control={control}
                                name="officecode"
                                rules={{ required: false }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Office Code"
                                    options={
                                      sfOfficeCodeOpt ? sfOfficeCodeOpt : []
                                    }
                                    isMulti={false}
                                    disabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                      setValue("officeName", e ? e.id : null);
                                      setValue(
                                        "businessOperation",
                                        e ? e?.name : null,
                                      );
                                      setValue("changeeffectivedate", null);
                                      setValue("phone", e ? e?.phone : null);

                                      if (
                                        userPermissions.includes(
                                          acquisitionName,
                                        )
                                      ) {
                                        setValue(
                                          "acquisitionName",
                                          e ? e?.officeAcquisition : "",
                                        );
                                      }
                                      if (e === null && !isSplc) {
                                        setValue("reportsToFullname", null);
                                        setValue(
                                          "reportsToEmployeeNumber",
                                          null,
                                        );
                                        setValue("reportsToEmployeeId", null);
                                      } else if (e.length > 0) {
                                        OfficeAutoReportsTo(e);
                                      }

                                      isAddState
                                        ? e
                                          ? setLocationChanged(true)
                                          : setLocationChanged(false)
                                        : setLocationChanged(true);
                                    }}
                                    value={field.value}
                                  />
                                )}
                              />

                              {/*A Note if selected Office is Closed and Emp is not terminated*/}
                              {selectedOfficeStatus === "Closed" &&
                                watch("status")?.toLowerCase() !==
                                  "terminated" && (
                                  <div className="grid md:grid-cols-3">
                                    <div></div>
                                    <div className="col-span-2 text-xs text-secondary italic">
                                      Office is already closed. Please choose a
                                      different office.
                                    </div>
                                  </div>
                                )}
                            </div>
                          )}
                          <div className="">
                            {requiredBilingual &&
                              addressRequired &&
                              notTerminated && (
                                <Controller
                                  control={control}
                                  name="associatedOfficeId"
                                  rules={{ required: true }}
                                  render={({ field }) => (
                                    <SelectHookFormSingle
                                      label="Associated Office"
                                      options={sfAsosciateToOpt}
                                      className={
                                        errors.associatedOfficeId &&
                                        "border-2 border-red-500 col-span-2"
                                      }
                                      isMulti={false}
                                      required={true}
                                      disabled={
                                        permissionContactEdit
                                          ? !permissionContactEdit
                                          : permissionContactAdd
                                            ? !permissionContactAdd
                                            : true
                                      }
                                      onChange={(e) => {
                                        field.onChange(e ? e.value : null);
                                      }}
                                      value={field?.value}
                                    />
                                  )}
                                />
                              )}
                          </div>
                        </div>
                        <div className="">
                          <div className="space-y-1">
                            <div className="space-y-1">
                              {user.isFranchise &&
                              watch("employeeTypeName")
                                ?.toLowerCase()
                                ?.includes("franchise") ? (
                                ""
                              ) : (
                                <TextInputHookFormDisabled
                                  name="phone"
                                  label="Phone"
                                  type="text"
                                  required={false}
                                  register={register("phone")}
                                />
                              )}
                              {!isSplc && (
                                <Controller
                                  control={control}
                                  name="reportsToEmployeeId"
                                  rules={{
                                    required:
                                      user?.isNonFranchise && user?.isFranchise
                                        ? watch("employeeTypeName")
                                            ?.toLowerCase()
                                            ?.includes("franchise")
                                          ? false
                                          : true
                                        : user?.isFranchise
                                          ? false
                                          : true,
                                  }}
                                  render={({ field }) => (
                                    <SelectHookFormSingle
                                      label="Reports To"
                                      className={
                                        errors.reportsToEmployeeId &&
                                        errors.reportsToEmployeeId &&
                                        "border-2 border-red-500 col-span-2"
                                      }
                                      onChange={(e) => {
                                        field.onChange(e ? e.value : null);
                                      }}
                                      value={field.value}
                                      options={reportsToOptions?.filter(
                                        (x) => x.isSplc !== true,
                                      )}
                                      isLoading={isReportsToOpsLoading}
                                      isMulti={false}
                                      required={
                                        user?.isNonFranchise &&
                                        user?.isFranchise
                                          ? watch("employeeTypeName")
                                              ?.toLowerCase()
                                              ?.includes("franchise")
                                            ? false
                                            : true
                                          : user?.isFranchise
                                            ? false
                                            : true
                                      }
                                    />
                                  )}
                                />
                              )}

                              {isAddState || isEditState ? (
                                ""
                              ) : (
                                <>
                                  <Controller
                                    control={control}
                                    name="reportstostatus"
                                    rules={{ required: false }}
                                    render={({ field }) => (
                                      <SelectHookFormSingle
                                        label="Reports To Status"
                                        options={reportsStatusOpt}
                                        isMulti={false}
                                        className={
                                          errors.reportsToFullname &&
                                          "border-2 border-red-500 col-span-2"
                                        }
                                        disabled={
                                          permissionContactEdit
                                            ? !permissionContactEdit
                                            : permissionContactAdd
                                              ? !permissionContactAdd
                                              : true
                                        }
                                        onChange={(e) => {
                                          field.onChange(e);
                                        }}
                                        value={field.value}
                                      />
                                    )}
                                  />
                                  <TextInputLabelHookForm
                                    name="reportstotermdate"
                                    label="Reports To Term Date"
                                    type="date"
                                    placeholder=""
                                    register={register("reportstotermdate", {})}
                                    disabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                  />
                                </>
                              )}
                              <Controller
                                control={control}
                                name="forwardemailsto"
                                rules={{ required: false }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Forward Emails To"
                                    options={employeesOpts}
                                    isMulti={false}
                                    disabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                    }}
                                    value={field.value}
                                    isLoading={isReportsToOpsLoading}
                                  />
                                )}
                              />
                              <TextInputLabelHookForm
                                name="businessOperation"
                                label="Business Operation"
                                type="text"
                                placeholder="Business Operation"
                                register={register("businessOperation", {})}
                                disabled={true}
                              />
                              {userPermissions.includes(acquisitionName) && (
                                <Controller
                                  control={control}
                                  name="acquisitionId"
                                  rules={{ required: false }}
                                  render={({ field }) => (
                                    <SelectHookFormSingle
                                      label="Acquisition Name"
                                      options={acquisitionNameOpt}
                                      value={field.value}
                                      className={
                                        errors.acquisitionId &&
                                        "border-2 border-red-500 col-span-2"
                                      }
                                      onChange={(e) => {
                                        field.onChange(e ? e.value : null);
                                      }}
                                      isLoading={acquisitionNameFetch}
                                    />
                                  )}
                                />
                              )}
                              <TextInputHookFormDisabled
                                label="Country"
                                type="text"
                                value={isMexico ? "Mexico" : "United States"}
                              />
                              {isMexico && (
                                <Controller
                                  control={control}
                                  name="adOverrideGroup"
                                  render={({ field }) => (
                                    <SelectHookFormMulti
                                      label="AD Override Group"
                                      options={overrideGroupOpts}
                                      isLoading={!overrideGroupOpts?.length}
                                      value={field.value || []}
                                      onChange={(e) => {
                                        field.onChange(e);
                                      }}
                                    />
                                  )}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {userPermissions.includes(viewAdditionalPermission) && (
                  <div name="additional-details">
                    <div className="m-2 p-2 bg-white box-shadow rounded-lg h-full">
                      <div className="text-quinary font-bold font-sans py-2 px-4">
                        Additional Details
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 px-4 lg:gap-12">
                        <div className="space-y-1 w-full pb-2">
                          <div>
                            {errors.employeenumber?.type === "minLength" && (
                              <span className="flex text-red-500 text-xs italic justify-end -mt-4">
                                {errors.employeenumber.message}
                              </span>
                            )}

                            <TextInputLabelHookForm
                              name="employeenumber"
                              label="Employee ID"
                              type="text"
                              className={
                                errors.employeenumber &&
                                errors.employeenumber.message &&
                                "border-2 border-red-500 "
                              }
                              minLength={6}
                              maxLength={250}
                              // title="Sample Format: 123456 (6 digits only)"
                              register={register("employeenumber", {})}
                              disabled={
                                permissionAdditionalDetailsEdit
                                  ? !permissionAdditionalDetailsEdit
                                  : permissionAdditionalDetailsAdd
                                    ? !permissionAdditionalDetailsAdd
                                    : true
                              }
                            />
                          </div>
                          <Controller
                            control={control}
                            name="bilingualId"
                            rules={{ required: requiredBilingual }}
                            render={({ field }) => (
                              <SelectHookFormSingle
                                label="Bilingual"
                                options={employeeBilingualOpt}
                                register={register("bilingualId", {
                                  required: {
                                    value: requiredBilingual,
                                    message:
                                      "Please complete for Sales Employees",
                                  },
                                })}
                                isMulti={false}
                                required={requiredBilingual}
                                className={
                                  errors.bilingualId &&
                                  "border-2 border-red-500 col-span-2"
                                }
                                disabled={
                                  permissionAdditionalDetailsEdit
                                    ? !permissionAdditionalDetailsEdit
                                    : permissionAdditionalDetailsAdd
                                      ? !permissionAdditionalDetailsAdd
                                      : true
                                }
                                onChange={(e) => {
                                  field.onChange(e ? e.value : null);
                                  setValue("bilingual", e ? e.label : null);
                                }}
                                value={field.value}
                              />
                            )}
                          />
                          {errors.bilingualId?.message ===
                          "Please complete for Sales Employees" ? (
                            <>
                              <Tooltip
                                title="Please complete for Sales Employees"
                                placement="top-end"
                                arrow
                                disableTriggerFocus={true}
                                open={true}
                              >
                                <InformationCircleIcon
                                  data-for="bilinguals"
                                  className="absolute top top-3 right-0 h-5 w-5 -mr-5 text-red-500"
                                />
                              </Tooltip>{" "}
                            </>
                          ) : (
                            ""
                          )}

                          <TextInputLabelHookForm
                            name="hiredate"
                            label="Hire Date"
                            type="date"
                            placeholder="Hire Date"
                            className={
                              errors.hiredate &&
                              errors.hiredate.message &&
                              "border-2 border-red-500 col-span-2"
                            }
                            register={register("hiredate", {
                              required: {
                                value: true,
                                message: "Required Field",
                              },
                            })}
                            onChange={(e) => {
                              setValue("hiredate", e.target.value);
                              setValue("rehireDate", null);
                              setValue("terminationdate", null);
                            }}
                            required={true}
                            disabled={
                              permissionAdditionalDetailsEdit
                                ? !permissionAdditionalDetailsEdit
                                : permissionAdditionalDetailsAdd
                                  ? !permissionAdditionalDetailsAdd
                                  : true
                            }
                          />
                          <TextInputLabelHookForm
                            name="terminationdate"
                            label="Termination Date"
                            type="date"
                            placeholder="Termination Date"
                            register={register("terminationdate", {
                              required: {
                                value: wasTerminated,
                                message: "Required Field",
                              },
                            })}
                            required={wasTerminated}
                            onChange={(e) => {
                              setValue("terminationdate", e.target.value);
                              setValue("rehireDate", null);
                            }}
                            min={watch("hiredate")}
                            className={
                              errors.terminationdate &&
                              errors.terminationdate.message &&
                              "border-2 border-red-500 col-span-2"
                            }
                            disabled={
                              permissionAdditionalDetailsEdit
                                ? !permissionAdditionalDetailsEdit
                                : permissionAdditionalDetailsAdd
                                  ? !permissionAdditionalDetailsAdd
                                  : true
                            }
                          />
                          <TextInputLabelHookForm
                            name="rehireDate"
                            label="Rehire Date"
                            type="date"
                            placeholder="Rehire Date"
                            min={
                              minRehireDate &&
                              minRehireDate?.toISOString()?.substr(0, 10)
                            }
                            register={register("rehireDate", {
                              required: {
                                value: rehire || watch("status")?.toLowerCase() === "rehire",
                                message: "Required Field",
                              },
                            })}
                            required={rehire || watch("status")?.toLowerCase() === "rehire"}
                            disabled={watch("terminationdate") ? false : true}
                            className={
                              errors.rehireDate &&
                              errors.rehireDate.message &&
                              "border-2 border-red-500 col-span-2"
                            }
                          />
                          <TextInputLabelHookForm
                            name="onLeaveEffectiveDate"
                            label="On Leave Effective Date"
                            type="date"
                            register={register("onLeaveEffectiveDate", {
                              required: {
                                value: onLeave,
                                message: "Required Field",
                              },
                            })}
                            required={onLeave}
                            disabled={
                              permissionAdditionalDetailsEdit
                                ? !permissionAdditionalDetailsEdit
                                : permissionAdditionalDetailsAdd
                                  ? !permissionAdditionalDetailsAdd
                                  : true
                            }
                          />
                          <TextInputLabelHookForm
                            name="leavereturndate"
                            label="Leave Return Date"
                            type="date"
                            register={register("leavereturndate", {
                              required: {
                                value: fromLeave,
                                message: "Required Field",
                              },
                            })}
                            className={
                              errors.leavereturndate &&
                              errors.leavereturndate.message &&
                              "border-2 border-red-500 col-span-2"
                            }
                            required={fromLeave}
                            disabled={
                              permissionAdditionalDetailsEdit
                                ? !permissionAdditionalDetailsEdit
                                : permissionAdditionalDetailsAdd
                                  ? !permissionAdditionalDetailsAdd
                                  : true
                            }
                          />
                          <TextInputLabelHookForm
                            name="changeeffectivedate"
                            label="Change Effective Date"
                            type="date"
                            placeholder="Change Effective Date"
                            register={register("changeeffectivedate", {
                              required: {
                                value:
                                  (titleChanged && isEditState) ||
                                  (locationChanged && isEditState),
                                message: "Required Field",
                              },
                            })}
                            // min={currentDate}
                            required={
                              (titleChanged && isEditState) ||
                              (locationChanged && isEditState)
                                ? true
                                : false
                            }
                            className={
                              errors.changeeffectivedate &&
                              errors.changeeffectivedate.message &&
                              "border-2 border-red-500 col-span-2"
                            }
                            disabled={
                              permissionAdditionalDetailsEdit
                                ? !permissionAdditionalDetailsEdit
                                : permissionAdditionalDetailsAdd
                                  ? !permissionAdditionalDetailsAdd
                                  : true
                            }
                          />
                        </div>
                        {isMexico && terminatedStatus && (
                          <div className="space-y-2 w-full">
                            {userPermissions.includes(isSeparationType) && (
                              <Controller
                                control={control}
                                name="separationTypeId"
                                rules={{
                                  required:
                                    isMexico && terminatedStatus
                                      ? "Required Field"
                                      : false,
                                }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Separation Type"
                                    options={separationTypeOpts}
                                    value={field.value}
                                    className={
                                      errors.separationTypeId &&
                                      "border-2 border-red-500 col-span-2"
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                      setValue("separationReasonId", null);
                                    }}
                                    isLoading={separationTypeLoading}
                                    required={isMexico && terminatedStatus}
                                  />
                                )}
                              />
                            )}
                            {userPermissions.includes(isSeparationReason) && (
                              <Controller
                                control={control}
                                name="separationReasonId"
                                rules={{
                                  required:
                                    isMexico &&
                                    (separationTypeId === 1 ||
                                      separationTypeId === 2)
                                      ? "Required Field"
                                      : false,
                                }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Separation Reason"
                                    options={separationReasonOpts}
                                    value={field.value}
                                    className={
                                      errors.separationReasonId
                                        ? "border-2 border-red-500 col-span-2"
                                        : ""
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                    }}
                                    isLoading={separationReasonLoading}
                                    required={
                                      isMexico &&
                                      (separationTypeId === 1 ||
                                        separationTypeId === 2)
                                    }
                                  />
                                )}
                              />
                            )}
                            {userPermissions.includes(isRehirable) && (
                              <Controller
                                control={control}
                                name="rehirableId"
                                rules={{
                                  required:
                                    isMexico && terminatedStatus
                                      ? "Required Field"
                                      : false,
                                }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Rehirable"
                                    options={rehirableOpts}
                                    value={field.value}
                                    className={
                                      errors.rehirableId &&
                                      "border-2 border-red-500 col-span-2"
                                    }
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                    }}
                                    isLoading={rehirableLoading}
                                    required={isMexico && terminatedStatus}
                                  />
                                )}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {isSplc && (
                  <div name="additional-details">
                    <div className="m-2 p-2 bg-white box-shadow rounded-lg h-full">
                      <div className="text-quinary font-bold font-sans py-2 px-4">
                        Hierarchy and Leadership
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 px-4 lg:gap-12">
                        <div className="space-y-1 w-full pb-2">
                          <TextInputHookFormDisabled
                            name="hierarchyChannel"
                            label="Hierarchy Channel"
                            value="SPLC"
                            type="text"
                          />
                          <Controller
                            control={control}
                            name="hierarchyLeadership"
                            rules={{ required: true }}
                            render={({ field }) => {
                              return (
                                <SelectHookFormSingle
                                  field={field}
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    setValue("reportsToHierarchyId", null);
                                    onReportsToChange(null);
                                  }}
                                  value={field.value}
                                  options={managerOptions}
                                  label="Hierarchy Leadership"
                                  className={
                                    errors.hierarchyLeadership &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  required={!watch("isHierarchyManager")}
                                  isDisabled={watch("isHierarchyManager")}
                                />
                              );
                            }}
                          />
                          {selectedLeadership !== "Executive" && (
                            <Controller
                              control={control}
                              name="reportsToHierarchyId"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  field={field}
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);

                                    const selectedHierarchy = e || null;
                                    onReportsToChange(selectedHierarchy);
                                  }}
                                  value={field.value}
                                  options={hierarchyOpts}
                                  isLoading={isHierarchyOptsLoading}
                                  label={
                                    <>
                                      <i>(Reports To)</i>&nbsp;
                                      {selectedLeadership || "Team Manager"}
                                    </>
                                  }
                                  className={
                                    errors.reportsToHierarchyId &&
                                    "border-2 border-red-500 col-span-2 "
                                  }
                                  required={true}
                                />
                              )}
                            />
                          )}
                          {selectedLeadership === "Executive" && (
                            <Controller
                              control={control}
                              name="reportsToEmployeeId"
                              rules={{
                                required:
                                  user?.isNonFranchise && user?.isFranchise
                                    ? watch("employeeTypeName")
                                        ?.toLowerCase()
                                        ?.includes("franchise")
                                      ? false
                                      : true
                                    : user?.isFranchise
                                      ? false
                                      : true,
                              }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Reports To"
                                  className={
                                    errors.reportsToEmployeeId &&
                                    errors.reportsToEmployeeId &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    setValue("reportsToHierarchyId", null);
                                  }}
                                  value={field.value}
                                  options={reportsToOptions?.filter((x) =>
                                    x.employeeTitle
                                      ?.toLowerCase()
                                      .includes("chief"),
                                  )}
                                  isLoading={isReportsToOpsLoading}
                                  isMulti={false}
                                  required={
                                    user?.isNonFranchise && user?.isFranchise
                                      ? watch("employeeTypeName")
                                          ?.toLowerCase()
                                          ?.includes("franchise")
                                        ? false
                                        : true
                                      : user?.isFranchise
                                        ? false
                                        : true
                                  }
                                />
                              )}
                            />
                          )}
                          <div className="space-y-2">
                            {leadership.includes("District Manager") && (
                              <TextInputHookFormDisabled
                                name="districtManager"
                                label="District Manager"
                                type="text"
                                register={register("districtManager")}
                              />
                            )}
                            {leadership.includes("Director") && (
                              <TextInputHookFormDisabled
                                name="director"
                                label="Director"
                                type="text"
                                register={register("director")}
                              />
                            )}
                            {leadership.includes("Vice President") && (
                              <TextInputHookFormDisabled
                                name="vicePresident"
                                label="Vice President"
                                type="text"
                                register={register("vicePresident")}
                              />
                            )}
                            {leadership.includes("Senior Vice President") && (
                              <TextInputHookFormDisabled
                                name="seniorVicePresident"
                                label="Senior Vice President"
                                type="text"
                                register={register("seniorVicePresident")}
                              />
                            )}
                          </div>
                        </div>
                        <div className="space-y-2 w-full">
                          <div className="h-[55px]"></div>
                          {visibleRoles.includes("Team Manager") && (
                            <>
                              <ReactTooltip
                                place="top"
                                effect="solid"
                                type="info"
                              />

                              <div
                                data-tip={
                                  watch("teamName")?.length > 50
                                    ? watch("teamName")
                                    : undefined
                                }
                              >
                                <TextInputHookFormDisabled
                                  name="teamName"
                                  label="Team Name"
                                  type="text"
                                  register={register("teamName")}
                                  value={
                                    watch("teamName")?.length > 50
                                      ? watch("teamName").slice(0, 50).trim() +
                                        "..."
                                      : (watch("teamName") ?? "")
                                  }
                                />
                              </div>
                            </>
                          )}
                          {visibleRoles.includes("District Manager") && (
                            <TextInputHookFormDisabled
                              name="districtName"
                              label="District"
                              type="text"
                              register={register("districtName")}
                            />
                          )}
                          {visibleRoles.includes("Director") && (
                            <TextInputHookFormDisabled
                              name="stateName"
                              label="State"
                              type="text"
                              register={register("stateName")}
                            />
                          )}
                          {visibleRoles.includes("Vice President") && (
                            <TextInputHookFormDisabled
                              name="marketName"
                              label="Market"
                              type="text"
                              register={register("marketName")}
                            />
                          )}
                          {visibleRoles.includes("Senior Vice President") && (
                            <TextInputHookFormDisabled
                              name="businessUnitName"
                              label="Business Unit"
                              type="text"
                              register={register("businessUnitName")}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {userPermissions.includes(viewAdditionalPermission) && (
                  <div name="contact-details ">
                    <div className="m-2 p-2 bg-white box-shadow rounded-lg h-full">
                      <div className="text-quinary font-bold font-sans py-2 px-4">
                        Address Information
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 text-xs px-4 lg:gap-12">
                        <div className="space-y-1 w-full">
                          <div className="space-y-1">
                            <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-2">
                              <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                                {addressRequired && (
                                  <div className="text-red-500 pr-0.5 font-bold">
                                    *
                                  </div>
                                )}{" "}
                                Address:
                              </label>

                              {isLoaded && (
                                <div className="col-span-2">
                                  <Controller
                                    control={control}
                                    name="shippingLocation"
                                    rules={{ required: addressRequired }}
                                    render={({ field }) => (
                                      <Autocomplete
                                        onLoad={onLoad}
                                        onPlaceChanged={OnPlacesChanged}
                                        restrictions={{
                                          country: [isMexico ? "mx" : "us"],
                                        }}
                                      >
                                        <input
                                          {...field}
                                          onChange={(e) => {
                                            field.onChange(e.target.value);
                                            if (!e.target.value) {
                                              clearAddress();
                                            }
                                          }}
                                          type="text"
                                          value={field.value}
                                          placeholder="Enter Location"
                                          className={
                                            errors.shippingLocation
                                              ? "border-2 border-red-500 focus:ring-septenary focus:border-septenar block w-full text-xs p-1 bg-white bg-clip-padding leading-normal outline-none block w-full "
                                              : "border border-gray-300 focus:ring-septenary focus:border-septenar block w-full text-xs p-1 bg-white bg-clip-padding leading-normal outline-none block w-full "
                                          }
                                        />
                                      </Autocomplete>
                                    )}
                                  />
                                </div>
                              )}
                            </div>
                            <TextInputHookFormWithKB
                              name="suiteNumber"
                              label="Suite Number"
                              type="text"
                              register={register("suiteNumber", {})}
                              disabled={false}
                              maxLength={50}
                              keyboardOptions={
                                keyboardOpts?.filter(
                                  (x) =>
                                    x.isCapital === isKeyboardCaps ||
                                    x.isCapital === null,
                                ) ?? []
                              }
                              isTressEmployee={isMexico}
                              showKeyboardRight={true}
                              isKeyboardCaps={isKeyboardCaps}
                              keyboardOnclick={(e) => {
                                handleKeyboardChange("suiteNumber", e);
                              }}
                              isKeyboardOpen={displayKeyboard.suiteNumber}
                              onClickShowKeyboard={(e) => {
                                handleKeyboardToggle("suiteNumber");
                              }}
                              onClickCloseKeyboard={handleCloseKeyboard}
                            />
                            <TextInputHookFormDisabled
                              name="address"
                              label="Street"
                              type="text"
                              required={addressRequired ? true : false}
                              register={register("address", {
                                required: {
                                  value: false,
                                  message: "Required Field",
                                },
                              })}
                            />
                            <>
                              <TextInputHookFormDisabled
                                name="city"
                                label="City"
                                type="text"
                                required={addressRequired ? true : false}
                                register={register("city")}
                              />
                              <TextInputHookFormDisabled
                                name="stateCode"
                                label="State"
                                type="text"
                                required={addressRequired ? true : false}
                                register={register("employeeState")}
                              />
                              <TextInputHookFormDisabled
                                name="zipcode"
                                label="ZIP Code"
                                type="text"
                                required={addressRequired ? true : false}
                                register={register("zipcode")}
                              />
                              <TextInputHookFormDisabled
                                name="country"
                                label="Country"
                                type="text"
                                required={addressRequired ? true : false}
                                register={register("country")}
                              />
                            </>
                          </div>
                        </div>
                        <div className="">
                          <div></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 w-full gap-2">
                  {userPermissions.includes(viewAdditionalPermission) && (
                    <div className="ml-2 p-2 bg-white box-shadow rounded-lg h-full">
                      <div className="text-quinary font-bold font-sans py-2 px-4">
                        Corporate Employee Information
                      </div>
                      <div className="grid grid-cols-1 text-xs px-4 gap-2 gap-x-12">
                        <div>
                          <div className="relative">
                            <Controller
                              control={control}
                              name="mirroremployee"
                              rules={{ required: false }}
                              render={({ field }) => (
                                <SelectHookFormSingle
                                  label="Mirror Employee"
                                  options={employeesOpts}
                                  className={
                                    errors.mirroremployee &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                  register={register("mirroremployee", {
                                    required: {
                                      value: requiredMirrorEmployee,
                                      message:
                                        "Please complete for Corporate Employees",
                                    },
                                  })}
                                  required={requiredMirrorEmployee}
                                  isMulti={false}
                                  disabled={
                                    permissionContactEdit
                                      ? !permissionContactEdit
                                      : permissionContactAdd
                                        ? !permissionContactAdd
                                        : true
                                  }
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                    setValue("mirroremployeeemail", e?.email);
                                    setValue("mirroremployeehrid", e?.id);
                                  }}
                                  value={field.value}
                                  isLoading={isReportsToOpsLoading}
                                />
                              )}
                            />
                            {errors.mirroremployee?.message ===
                            "Please complete for Corporate Employees" ? (
                              <>
                                <Tooltip
                                  title="Please complete for Corporate Employees"
                                  placement="top-end"
                                  arrow
                                  disableTriggerFocus={true}
                                  open={true}
                                >
                                  <InformationCircleIcon
                                    data-tip={
                                      "Please complete for Corporate Employees"
                                    }
                                    data-for="mirroremployees"
                                    className="absolute top top-1 right-0 h-5 w-5 -mr-5 text-red-500"
                                  />
                                </Tooltip>{" "}
                              </>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>

                        <TextInputLabelHookForm
                          name="mirroremployeeemail"
                          label="Mirror Employee Email"
                          type="text"
                          disabled={true}
                          placeholder=""
                          register={register("mirroremployeeemail", {})}
                        />
                        <TextInputLabelHookForm
                          name="mirroremployeehrid"
                          label="Mirror Employee HR ID"
                          type="text"
                          disabled={true}
                          placeholder=""
                          register={register("mirroremployeehrid", {})}
                        />
                        <Controller
                          name="emailDomainId"
                          control={control}
                          rules={{
                            required:
                              watch("employeeTypeName") === "Support" &&
                              watch("status") === "New Hire"
                                ? true
                                : false,
                          }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              label="Email Domain"
                              options={emailDomainsOpt ? emailDomainsOpt : []}
                              isLoading={isGetEmailDomainsLoading}
                              className={
                                errors.emailDomainId &&
                                "border-2 border-red-500 col-span-2"
                              }
                              required={
                                watch("employeeTypeName") === "Support" &&
                                watch("status") === "New Hire"
                                  ? true
                                  : false
                              }
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                  {userPermissions.includes(viewTrainingPermission) && (
                    <div name="trainings">
                      <div className="p-2 mr-2 bg-white box-shadow rounded-lg h-full">
                        <div className="text-quinary font-bold font-sans py-2 px-4">
                          Training and Licensing
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-1  w-full  items-center px-4">
                          <div className="p-1 w-full text-xs">
                            <div className="">
                              <Controller
                                control={control}
                                name="traininglocation"
                                rules={{ required: false }}
                                render={({ field }) => (
                                  <SelectHookFormSingle
                                    label="Training Location"
                                    options={
                                      sfOfficeNameOpt ? sfOfficeNameOpt : []
                                    }
                                    isMulti={false}
                                    disabled={
                                      permissionContactEdit
                                        ? !permissionContactEdit
                                        : permissionContactAdd
                                          ? !permissionContactAdd
                                          : true
                                    }
                                    isLoading={officeIsLoading}
                                    onChange={(e) => {
                                      field.onChange(e ? e.value : null);
                                    }}
                                    value={field.value}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className={isConfirming && "pointer-events-none"}>
            <ConfirmBoxModal
              ModalTitle={isAddState ? "Add Employee" : "Edit Employee"}
              qConfirmation={`Are you sure you want to ${
                isAddState ? "save " : "update "
              } this data?`}
              isOpen={saveModal}
              onClose={() => {
                setSaveModal(false);
                setIsSaving(false);
              }}
              onClickFunction={() => {
                handleSave();
                setIsSaving(true);
              }}
            />
          </div>

          <div className={isConfirming && "pointer-events-none"}>
            <ConfirmBoxModal
              ModalTitle={"Duplicate Employee"}
              qConfirmation={`There is an existing employee with the same name. Are you sure you want to ${
                isAddState ? "save " : "update "
              } this data?`}
              isOpen={duplicateModal}
              onClose={() => {
                setDuplicateModal(false);
                setIsSaving(false);
              }}
              onClickFunction={() => {
                handleSave();
                setIsSaving(true);
              }}
            />
          </div>
        </div>
      ) : (
        <div className="h-96">
          <CustomLoader />
        </div>
      )}
    </div>
  );
};

EmployeeForm.propTypes = {
  reportsToOptions: PropTypes.array,
  employeesOpts: PropTypes.array,
  isReportsToOpsLoading: PropTypes.bool,
  overrideGroupOpts: PropTypes.array,
  setOnClose: PropTypes.func,
  onClickCancelShow: PropTypes.func,
  hasUSAdd: PropTypes.bool,
  mexicoAdd: PropTypes.bool,
  onSaveSuccess: PropTypes.func,
};

export default EmployeeForm;
